import type { MetaConversation } from '@/lib/stores/metaMessagingStore';

export type MetaWindowState = 'standard' | 'human_agent' | 'blocked';

const HOUR = 60 * 60 * 1000;
const DAY = 24 * HOUR;

/**
 * Stessa regola del core (`computeMessagingWindow`): la finestra decorre
 * dall'ultimo messaggio IN ARRIVO. Entro 24 ore risposta libera, entro 7 giorni
 * solo con il tag operatore umano (che Meta deve aver approvato), oltre bloccata.
 * Conversazione senza inbound (dati vecchi) = bloccata, mai un invio in violazione.
 */
export function windowState(conversation: Pick<MetaConversation, 'last_inbound_at'>, now: Date = new Date()): MetaWindowState {
  if (!conversation.last_inbound_at) return 'blocked';
  const elapsed = now.getTime() - new Date(conversation.last_inbound_at).getTime();
  if (elapsed <= DAY) return 'standard';
  if (elapsed <= 7 * DAY) return 'human_agent';
  return 'blocked';
}

/** Testo breve per il composer: quanto resta della finestra. */
export function windowLabel(conversation: Pick<MetaConversation, 'last_inbound_at'>, now: Date = new Date()): string {
  const state = windowState(conversation, now);
  if (state === 'blocked') return 'Finestra di risposta scaduta: puoi rispondere solo dopo un nuovo messaggio del contatto';
  const last = new Date(conversation.last_inbound_at as string).getTime();
  if (state === 'standard') {
    const left = Math.max(1, Math.round((last + DAY - now.getTime()) / HOUR));
    return `Puoi rispondere ancora per ${left} ${left === 1 ? 'ora' : 'ore'}`;
  }
  const leftDays = Math.max(1, Math.round((last + 7 * DAY - now.getTime()) / DAY));
  return `Oltre le 24 ore: risposta come operatore umano, ancora per ${leftDays} ${leftDays === 1 ? 'giorno' : 'giorni'}`;
}

/** L'ultimo messaggio è del contatto: la conversazione aspetta una risposta. */
export function needsReply(conversation: Pick<MetaConversation, 'last_inbound_at' | 'last_message_at'>): boolean {
  if (!conversation.last_inbound_at) return false;
  if (!conversation.last_message_at) return true;
  return new Date(conversation.last_inbound_at).getTime() >= new Date(conversation.last_message_at).getTime();
}

/**
 * Nome da mostrare. L'ingest non conosce il nome del contatto (Meta non lo
 * manda nel webhook): si usa il cliente collegato, altrimenti un'etichetta con
 * la coda dell'id esterno, che resta stabile per riconoscere la conversazione.
 */
export function conversationLabel(
  conversation: Pick<MetaConversation, 'channel' | 'contact_name' | 'external_contact_id'> & { profile_username?: string | null },
  linkedClientName?: string | null
): string {
  if (linkedClientName) return linkedClientName;
  if (conversation.contact_name) return conversation.contact_name;
  if (conversation.profile_username) return `@${conversation.profile_username}`;
  const tail = conversation.external_contact_id.slice(-6);
  return conversation.channel === 'instagram' ? `Contatto Instagram ${tail}` : `Contatto Messenger ${tail}`;
}

/** "adesso", "5 min", "3 h", "2 g", altrimenti data corta. */
export function relativeTime(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return '';
  const diff = now.getTime() - new Date(iso).getTime();
  if (diff < 60 * 1000) return 'adesso';
  if (diff < HOUR) return `${Math.floor(diff / (60 * 1000))} min`;
  if (diff < DAY) return `${Math.floor(diff / HOUR)} h`;
  if (diff < 7 * DAY) return `${Math.floor(diff / DAY)} g`;
  return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' });
}

export function messageTimeLabel(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

export const CHANNEL_LABEL: Record<'instagram' | 'messenger', string> = {
  instagram: 'Instagram',
  messenger: 'Messenger',
};

/** Chiave del giorno (locale) per i separatori del thread. */
export function dayKey(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

/** "Oggi", "Ieri", "lunedì 22 settembre": come i separatori delle chat WhatsApp. */
export function dayLabel(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  const start = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diffDays = Math.round((start(now) - start(d)) / (24 * 60 * 60 * 1000));
  if (diffDays === 0) return 'Oggi';
  if (diffDays === 1) return 'Ieri';
  return d.toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', ...(d.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {}) });
}
